import { Divider } from '@mui/material';
import { MemreButton, MemreFlexBox } from 'components/core';
import MemreIcon, { IconName } from 'components/core/MemreIcon';
import * as Color from 'core/colors';
import { Flashcards } from 'services/useItem.service/interactions';
import { useAuthoringContext } from '../AuthoringContext';
import AttachInstructionalItem from '../components/AttachInstructionalItem';
import GraduatedDifficulty from '../components/GraduatedDifficulty';
import { IncorrectAnswers } from '../components/IncorrectAnswers';
import { AnswerField } from '../components/AnswerField';
import { TConcept } from 'types';
import { useDeleteFacet } from 'hooks/useDeleteFacet';
import { useCreateFacet } from 'hooks/useCreateFacet';

export default function FlashcardsEditor() {
  const { item, set: assignment } = useAuthoringContext();
  const flashcardItem = item?.templateType === 'associations' ? (item as Flashcards) : null;
  const anchor: TConcept | null = flashcardItem?.rawItem.facets[0]?.anchor;
  const associations: TConcept[] = flashcardItem?.rawItem.facets.map((facet) => facet.association) ?? [];
  const [deleteFacet] = useDeleteFacet('deleteFacet', assignment?.id || '', item?.id || '');
  const [createFacet] = useCreateFacet(item?.id || '', assignment?.id || '');

  if (!flashcardItem || !anchor) {
    return <div>Loading...</div>;
  }
  const rawItem = flashcardItem.rawItem;

  const showDeleteButton = associations.length > 1;
  const didClickDelete = async (conceptId: string) => {
    const facet = rawItem.facets.find((f) => f.association.id === conceptId);
    if (facet) {
      await deleteFacet(facet.id);
      flashcardItem.updateRawItem({
        ...rawItem,
        facets: rawItem.facets.filter((f) => f.id !== facet.id),
      });
    }
  };
  const didClickAddCardBack = async () => {
    const newFacet = await createFacet({
      itemId: flashcardItem.id,
    } as any);
    console.log('New facet created:', newFacet);
    flashcardItem.updateRawItem({
      ...rawItem,
      facets: [...rawItem.facets, newFacet],
    });
  };

  return (
    <MemreFlexBox
      direction="column"
      gap={2}
      sx={{
        p: 2,
        pl: 0,
        width: '100%',
      }}
    >
      {/* Card Front */}
      <MemreFlexBox direction="column" gap={2} sx={{ width: '100%' }}>
        <h3>Card Front</h3>
        <AnswerField
          concept={anchor}
          label={`Card Front`}
          updateAnswerLocally={flashcardItem.updateConceptLocally}
          backgroundColor={'white'}
          allowSound={true}
        />
        <IncorrectAnswers
          conceptIdForDistractors={anchor.id}
          questionForSuggestedDistractors={anchor.text || ''}
          correctAnswersForSuggestedDistractors={anchor.text ? [anchor.text] : []}
        />
      </MemreFlexBox>

      <Divider sx={{ width: '100%', m: 0.5 }} />

      {/* Card Backs */}
      <MemreFlexBox direction="column" gap={2} sx={{ width: '100%' }}>
        <h3>Card Backs</h3>
        {associations.map((association, index) => (
          <CardBack
            key={`${association.id}_${index}`}
            concept={association}
            backNumber={index + 1}
            updateConceptLocally={flashcardItem.updateConceptLocally}
            didClickDelete={showDeleteButton ? () => didClickDelete(association.id) : undefined}
          />
        ))}
        <MemreButton
          variant="text"
          text="Add Card Back"
          size="small"
          iconName={IconName.plus}
          onClick={didClickAddCardBack}
        />
      </MemreFlexBox>

      <Divider sx={{ width: '100%', m: 0.5, mt: 2 }} />

      {/* Instructional Content */}
      {flashcardItem?.id && <AttachInstructionalItem itemId={flashcardItem.id} />}

      <Divider sx={{ width: '100%', m: 0.5, mt: 2 }} />

      {/* Graduated Difficulty */}
      {flashcardItem?.id && <GraduatedDifficulty itemId={flashcardItem.id} />}
    </MemreFlexBox>
  );
}

interface CardBackProps {
  updateConceptLocally: (answerId: string, updates: Partial<TConcept>) => void;
  concept: TConcept;
  backNumber: number;
  // If not provided, no delete button will be shown.
  didClickDelete?: () => void;
}
const CardBack = (props: CardBackProps) => {
  const { concept, backNumber, updateConceptLocally, didClickDelete } = props;
  const backgroundColor = Color.veryLightGray;

  // TODO: Newly added wrong answers disappear from UI (but are still there upon refresh) when
  // the user deletes a card back that isn't the last one causing the card back numbers to change.

  return (
    <>
      <MemreFlexBox direction="column" gap={2} sx={{ borderRadius: 2, p: 2, backgroundColor }}>
        {/* Card Back Header */}
        <MemreFlexBox justify="space-between" align="center" sx={{ width: '100%', mb: 1 }}>
          <h4 style={{ margin: 0 }}>Card Back {backNumber}</h4>
          {didClickDelete && (
            <MemreButton
              variant="icon"
              iconName={IconName.trash}
              size="medium"
              onClick={didClickDelete}
              aria-label={`Delete Card Back ${backNumber}`}
              sx={{
                color: Color.darkGreen,
                '&:hover': { color: Color.regularRed },
                '& svg': { fontSize: '1.25rem' }, // Increase icon size
              }}
            />
          )}
        </MemreFlexBox>

        <AnswerField
          concept={concept}
          label={`Card Back ${backNumber}`}
          updateAnswerLocally={updateConceptLocally}
          backgroundColor={backgroundColor}
          allowSound={true}
        />
        <IncorrectAnswers
          conceptIdForDistractors={concept.id}
          backgroundColor={backgroundColor}
          questionForSuggestedDistractors={concept.text || ''}
          correctAnswersForSuggestedDistractors={concept.text ? [concept.text] : []}
        />
      </MemreFlexBox>
    </>
  );
};
